import { Skeleton } from "@chakra-ui/react";
import { useInResiStore } from "@superbright/indexeddb-orm";
import { useEffect, useRef, useState } from "react";

import { useBootstrap } from "api/bootstrap/useBootstrap";
import { useFiltersQuery } from "api/filters/useFiltersAndUnits";
import {
  getPropertyQueryKey,
  usePropertyQuery,
} from "api/property/usePropertyQuery";
import { useStoredVisitorId, useVisitorSession } from "api/visitor-session";
import { QuestionnaireView } from "features/questionnaire";
import { InternalErrorResult, useErrorPageShown } from "shared/Result";
import { useNavigate, useParams, useRouteError } from "shared/Router";
import { queryClient } from "utils";

// Backstop for the bootstrap wait: fall open to the questionnaire.
const BOOTSTRAP_DECISION_TIMEOUT_MS = 2500;

// Per document, not per mount: minting a session for a new visitor remounts
// the tree, and that must not restart the wait. Client-only.
let knownNewVisitor = false;

interface IProps {
  fallbackProductsNumber?: number;
}

const HomePage = ({
  fallbackProductsNumber: _fallbackProductsNumber,
}: IProps) => {
  const { propertySlug: propertySlugParam } = useParams();
  const propertySlug = propertySlugParam || "";

  // Move filtersParams state here
  const [filtersParams, setFiltersParams] = useState(
    () => useInResiStore.getState().filters
  );

  // Move unitsEnabled state here if needed
  const [, setUnitsEnabled] = useState(false);

  const filtersQuery = useFiltersQuery(propertySlug, filtersParams);

  const navigate = useNavigate();
  const visitorId = useVisitorSession(propertySlug);
  const { data: bootstrapData } = useBootstrap(propertySlug, visitorId);

  // Already answered: redirect to results. The render is gated on this too, so
  // the questionnaire never paints before the effect-driven redirect lands.
  const questionnaireCompleted =
    !!propertySlug && bootstrapData?.questionnaire != null;

  useEffect(() => {
    if (questionnaireCompleted) {
      navigate("results", { replace: true });
    }
  }, [questionnaireCompleted, navigate]);

  // Without the visitor cookie (cross-site frame) the server cannot seed
  // bootstrap, so wait for the client answer. A browser with no stored visitor
  // id has never been here and does not wait.
  const storedVisitorId = useStoredVisitorId();
  if (storedVisitorId === null && typeof window !== "undefined") {
    knownNewVisitor = true;
  }
  const [decisionTimedOut, setDecisionTimedOut] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(
      () => setDecisionTimedOut(true),
      BOOTSTRAP_DECISION_TIMEOUT_MS
    );
    return () => window.clearTimeout(timer);
  }, []);

  const awaitingDecision =
    !knownNewVisitor &&
    storedVisitorId !== null &&
    bootstrapData === undefined &&
    !decisionTimedOut;

  // Only fetch when URL changes, not when filters change
  const { data, isLoading, error } = usePropertyQuery(propertySlug, {
    enabled: !!propertySlug,
  });

  // Keep track of URL changes
  // Invalidate property cache when the URL slug changes during SPA navigation.
  // Skip the very first render so SSR-seeded data (set by `pages/+Page.tsx`)
  // is preserved instead of immediately refetched in `useEffect`.
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    if (propertySlug) {
      queryClient.invalidateQueries({
        queryKey: getPropertyQueryKey(propertySlug),
      });
    }
  }, [propertySlug]);

  // Redirecting, or still deciding: never paint the questionnaire underneath.
  if (questionnaireCompleted || awaitingDecision) {
    return <Skeleton />;
  }

  if (isLoading || filtersQuery.isLoading) {
    return <Skeleton />;
  }

  if (error) {
    return <ErrorBoundary error={error} />;
  }

  return (
    <QuestionnaireView
      propertyData={data}
      filtersData={filtersQuery.data}
      setFiltersParams={(filters) => setFiltersParams(filters)}
      setUnitsEnabled={setUnitsEnabled}
    />
  );
};

export const Component = HomePage;

export const ErrorBoundary = ({ error: queryError }: { error?: unknown }) => {
  const routeError = useRouteError();
  const error = (queryError ?? routeError) as {
    status?: number;
    message?: string;
  };
  useErrorPageShown({
    statusCode: typeof error.status === "number" ? error.status : undefined,
    errorMessage: error.message,
    pageName: "Something went wrong",
  });

  // Only a route-level 404 falls back to the page; a failed request would just
  // error again and recurse, so it always gets the error screen
  if (!queryError && routeError.status === 404) {
    return <HomePage fallbackProductsNumber={20} />;
  }

  return <InternalErrorResult />;
};
